import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, BookOpen, MessageSquare, Clock,
  Video, CheckCircle, Loader2, ExternalLink, CreditCard,
  CalendarDays, ChevronRight, Zap, GraduationCap,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRelativeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const diffH = diffMs / 3600000;
  const diffD = Math.floor(diffH / 24);
  if (diffH < 0) return 'Started';
  if (diffH < 1) return `In ${Math.round(diffH * 60)}min`;
  if (diffH < 24) return `In ${Math.floor(diffH)}h ${Math.round((diffH % 1) * 60)}min`;
  if (diffD === 1) return 'Tomorrow';
  if (diffD < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const formatClassTime = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });

// The single most important action the student should take right now
const deriveNextStep = ({ billing, dashboardData, stripeEnabled }) => {
  const renewLink = stripeEnabled ? '/student/checkout' : '/student/billing';
  const enrollment = billing?.active_enrollment;

  // No plan at all
  if (!enrollment && !billing?.enrollment_history?.length) {
    return {
      type: 'get_plan',
      title: 'Start learning English',
      body: "You don't have an active plan yet. Choose a plan to get access to classes.",
      cta: 'Choose a plan',
      link: renewLink,
      color: 'navy',
    };
  }

  // Plan expired
  if (!enrollment && billing?.enrollment_history?.some(e => e.status === 'expired')) {
    return {
      type: 'renew',
      title: 'Your plan has expired',
      body: 'Renew to continue your English journey without losing momentum.',
      cta: 'Renew now',
      link: renewLink,
      color: 'red',
    };
  }

  // Zero credits left
  if (enrollment) {
    const remaining = enrollment.credits_total - enrollment.credits_used;
    if (remaining <= 0) {
      return {
        type: 'renew',
        title: 'No lessons remaining',
        body: "You've used all your credits. Renew to keep learning.",
        cta: 'Renew plan',
        link: renewLink,
        color: 'red',
      };
    }
    if (remaining === 1) {
      return {
        type: 'low_credits',
        title: 'Last lesson credit',
        body: 'You have 1 lesson left. Renew now to avoid any gap in your schedule.',
        cta: 'Renew plan',
        link: renewLink,
        color: 'orange',
      };
    }
  }

  // Class starting very soon (< 30min)
  const next = dashboardData?.upcoming_classes?.[0];
  if (next) {
    const diffMin = (new Date(next.scheduled_at) - Date.now()) / 60000;
    if (diffMin <= 30 && diffMin >= 0) {
      return {
        type: 'class_imminent',
        title: 'Your class is about to start!',
        body: `"${next.title}" — ${formatRelativeDate(next.scheduled_at)}`,
        cta: next.meeting_link ? 'Join class now' : 'Open portal',
        link: next.meeting_link || '/student/classes',
        external: !!next.meeting_link,
        color: 'green',
        class: next,
      };
    }
  }

  // No upcoming classes — nudge to book
  if (!next) {
    return {
      type: 'schedule',
      title: 'No classes scheduled',
      body: 'Schedule a 1:1 lesson with your teacher or join an open group class.',
      cta: 'Schedule a class',
      link: '/student/schedule',
      color: 'navy',
    };
  }

  // All good — show next class
  return { type: 'ok', class: next };
};

const STEP_COLORS = {
  navy: {
    bg: 'bg-gradient-to-r from-[#1A2E6E] to-[#2A4A8E]',
    badge: 'bg-white/10 text-white',
    cta: 'bg-white text-[#1A2E6E] hover:bg-white/90',
  },
  red: {
    bg: 'bg-gradient-to-r from-[#D72B2B] to-[#B71C1C]',
    badge: 'bg-white/10 text-white',
    cta: 'bg-white text-[#D72B2B] hover:bg-white/90',
  },
  orange: {
    bg: 'bg-gradient-to-r from-[#C97D0A] to-[#F5A623]',
    badge: 'bg-white/10 text-white',
    cta: 'bg-white text-[#C97D0A] hover:bg-white/90',
  },
  green: {
    bg: 'bg-gradient-to-r from-[#388E3C] to-[#4CAF50]',
    badge: 'bg-white/10 text-white',
    cta: 'bg-white text-[#388E3C] hover:bg-white/90',
  },
};

export const StudentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [billing, setBilling] = useState(null);
  const { user, isApproved } = useAuth();
  const { settings } = usePublicSettings();
  const contactEmail = settings.contact_email || 'franklytalkingenglish@gmail.com';
  const stripeEnabled = !!settings.stripe_enabled;

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      if (!isApproved) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const [dashboardResult, billingResult] = await Promise.allSettled([
        axios.get(`${API_URL}/api/student/dashboard`),
        axios.get(`${API_URL}/api/student/billing`),
      ]);

      if (!active) return;

      if (dashboardResult.status === 'fulfilled') {
        setDashboardData(dashboardResult.value.data);
      } else {
        setDashboardData(null);
        toast.error('Failed to load dashboard');
      }

      if (billingResult.status === 'fulfilled') {
        setBilling(billingResult.value.data);
      } else {
        setBilling(null);
      }

      setLoading(false);
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [isApproved]);

  // Pending state
  if (!isApproved) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="student-pending-approval">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#E8EAF0] p-8 text-center"
        >
          <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-[#F5A623]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora'] mb-4">
            Account Pending Approval
          </h1>
          <p className="text-[#1A2E6E]/70 mb-6 max-w-md mx-auto">
            Thank you for registering! Your account is currently being reviewed.
            You'll gain full access once approved.
          </p>
          <div className="bg-[#F5A623]/5 border border-[#F5A623]/20 rounded-xl p-4 text-left">
            <h3 className="font-semibold text-[#1A2E6E] mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-[#1A2E6E]/70">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#4CAF50] mt-0.5 shrink-0" />
                Our team will review your registration
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#4CAF50] mt-0.5 shrink-0" />
                You'll gain access to classes, activities, and materials
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#4CAF50] mt-0.5 shrink-0" />
                Start learning English with us!
              </li>
            </ul>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E8EAF0]">
            <p className="text-sm text-[#1A2E6E]/60">
              Questions?{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#D72B2B] hover:underline">
                {contactEmail}
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  const enrollment = billing?.active_enrollment;
  const creditsRemaining = enrollment ? enrollment.credits_total - enrollment.credits_used : null;
  const creditsTotal = enrollment?.credits_total || 1;
  const creditsPct = enrollment ? Math.round(((creditsTotal - (creditsTotal - creditsRemaining)) / creditsTotal) * 100) : 0;

  const nextStep = deriveNextStep({ billing, dashboardData, stripeEnabled });
  const nextClass = dashboardData?.upcoming_classes?.[0];
  const classesCompleted = dashboardData?.classes_completed ?? 0;

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'},{' '}
          {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#1A2E6E]/50 mt-1 text-sm">
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Next Step Hero */}
      {nextStep.type !== 'ok' && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-5 lg:p-6 text-white ${STEP_COLORS[nextStep.color]?.bg}`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={14} className="opacity-80" />
                <span className="text-xs font-semibold uppercase tracking-wider opacity-80">Next Step</span>
              </div>
              <h2 className="text-lg font-bold mb-1">{nextStep.title}</h2>
              <p className="text-sm opacity-80">{nextStep.body}</p>
            </div>
            {nextStep.external ? (
              <a
                href={nextStep.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors ${STEP_COLORS[nextStep.color]?.cta}`}
              >
                {nextStep.cta} <ExternalLink size={14} />
              </a>
            ) : (
              <Link
                to={nextStep.link}
                className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors ${STEP_COLORS[nextStep.color]?.cta}`}
              >
                {nextStep.cta} <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </motion.div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Credits */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-[#E8EAF0] p-4 col-span-2 lg:col-span-1"
        >
          <Link to="/student/billing" className="block group">
            <div className="flex items-center justify-between mb-3">
              <CreditCard className="w-5 h-5 text-[#4CAF50]" />
              <span className="inline-flex items-center gap-1 text-xs text-[#1A2E6E]/40 group-hover:text-[#D72B2B] transition-colors">
                My plan <ArrowRight size={12} />
              </span>
            </div>
            {enrollment ? (
              <>
                <p className="text-2xl font-bold text-[#1A2E6E]">{creditsRemaining}</p>
                <p className="text-sm text-[#1A2E6E]/60 mb-2">
                  {creditsRemaining === 1 ? 'lesson left' : 'lessons left'}
                </p>
                <div className="h-1.5 bg-[#E8EAF0] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      creditsRemaining <= 1 ? 'bg-[#D72B2B]' :
                      creditsRemaining <= 3 ? 'bg-[#F5A623]' : 'bg-[#4CAF50]'
                    }`}
                    style={{ width: `${creditsPct}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-[#1A2E6E]/30">—</p>
                <p className="text-sm text-[#1A2E6E]/50">No active plan</p>
              </>
            )}
          </Link>
        </motion.div>

        {/* Completed */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-[#E8EAF0] p-4"
        >
          <GraduationCap className="w-5 h-5 text-[#1A2E6E] mb-3" />
          <p className="text-2xl font-bold text-[#1A2E6E]">{classesCompleted}</p>
          <p className="text-sm text-[#1A2E6E]/60">
            {classesCompleted === 1 ? 'class done' : 'classes done'}
          </p>
        </motion.div>

        {/* Pending activities */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-[#E8EAF0] p-4"
        >
          <Link to="/student/activities" className="block group">
            <BookOpen className={`w-5 h-5 mb-3 ${dashboardData?.pending_activities?.length ? 'text-[#F5A623]' : 'text-[#1A2E6E]/30'}`} />
            <p className="text-2xl font-bold text-[#1A2E6E]">{dashboardData?.pending_activities?.length || 0}</p>
            <p className="text-sm text-[#1A2E6E]/60 group-hover:text-[#D72B2B] transition-colors">
              {dashboardData?.pending_activities?.length === 1 ? 'activity due' : 'activities due'}
            </p>
          </Link>
        </motion.div>

        {/* Messages */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-xl border border-[#E8EAF0] p-4"
        >
          <Link to="/student/messages" className="block group">
            <div className="relative inline-block mb-3">
              <MessageSquare className={`w-5 h-5 ${dashboardData?.unread_messages ? 'text-[#D72B2B]' : 'text-[#1A2E6E]/30'}`} />
              {dashboardData?.unread_messages > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#D72B2B] rounded-full" />
              )}
            </div>
            <p className="text-2xl font-bold text-[#1A2E6E]">{dashboardData?.unread_messages || 0}</p>
            <p className="text-sm text-[#1A2E6E]/60 group-hover:text-[#D72B2B] transition-colors">
              {dashboardData?.unread_messages === 1 ? 'unread message' : 'unread messages'}
            </p>
          </Link>
        </motion.div>
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left: Upcoming classes (3 col) */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#1A2E6E]" />
                <h2 className="font-semibold text-[#1A2E6E] text-sm">Upcoming Classes</h2>
              </div>
              <Link to="/student/classes" className="text-xs text-[#D72B2B] hover:text-[#B71C1C] font-medium flex items-center gap-1">
                All classes <ChevronRight size={12} />
              </Link>
            </div>
            <div className="divide-y divide-[#E8EAF0]">
              {dashboardData?.upcoming_classes?.length > 0 ? (
                dashboardData.upcoming_classes.slice(0, 4).map((cls, i) => {
                  const diffMin = (new Date(cls.scheduled_at) - Date.now()) / 60000;
                  const isImminent = diffMin >= 0 && diffMin <= 60;
                  return (
                    <motion.div
                      key={cls.id || i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.04 }}
                      className={`flex items-center gap-4 px-5 py-4 ${isImminent ? 'bg-[#4CAF50]/3' : ''}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        isImminent ? 'bg-[#4CAF50]/15' : 'bg-[#1A2E6E]/8'
                      }`}>
                        <Video className={`w-5 h-5 ${isImminent ? 'text-[#4CAF50]' : 'text-[#1A2E6E]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1A2E6E] text-sm truncate">{cls.title}</p>
                        <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                          {formatClassTime(cls.scheduled_at)}
                          {cls.teacher_name && ` · ${cls.teacher_name}`}
                        </p>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1.5">
                        <span className={`text-xs font-semibold ${isImminent ? 'text-[#4CAF50]' : 'text-[#1A2E6E]/50'}`}>
                          {formatRelativeDate(cls.scheduled_at)}
                        </span>
                        {cls.meeting_link && (
                          <a
                            href={cls.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1 bg-[#4CAF50] text-white text-xs font-semibold rounded-lg hover:bg-[#388E3C] transition-colors"
                          >
                            Join <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="px-5 py-10 text-center">
                  <CalendarDays className="w-10 h-10 text-[#1A2E6E]/15 mx-auto mb-3" />
                  <p className="text-sm text-[#1A2E6E]/50 mb-3">No upcoming classes</p>
                  <Link
                    to="/student/schedule"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1A2E6E] text-white text-xs font-semibold rounded-lg hover:bg-[#16275C] transition-colors"
                  >
                    <CalendarDays size={12} /> Schedule a class
                  </Link>
                  <Link
                    to="/student/available"
                    className="block mt-3 text-xs text-[#1A2E6E]/50 hover:text-[#D72B2B] transition-colors"
                  >
                    Prefer a group session? View open classes
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Grades */}
          {dashboardData?.recent_grades?.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#4CAF50]" />
                  <h2 className="font-semibold text-[#1A2E6E] text-sm">Recent Grades</h2>
                </div>
                <Link to="/student/activities" className="text-xs text-[#D72B2B] font-medium flex items-center gap-1">
                  All <ChevronRight size={12} />
                </Link>
              </div>
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {dashboardData.recent_grades.map((g, i) => (
                  <div key={g.id || i} className="p-3 bg-[#FAFAF7] rounded-xl">
                    <p className="text-xs font-medium text-[#1A2E6E] truncate mb-2">{g.title}</p>
                    <div className="flex items-end justify-between">
                      <span className={`text-xl font-bold ${
                        g.grade >= 80 ? 'text-[#4CAF50]' : g.grade >= 60 ? 'text-[#F5A623]' : 'text-[#D72B2B]'
                      }`}>
                        {g.grade}<span className="text-xs font-normal text-[#1A2E6E]/40">%</span>
                      </span>
                      {g.feedback && (
                        <span className="text-[10px] text-[#1A2E6E]/40 bg-[#1A2E6E]/5 px-1.5 py-0.5 rounded">
                          Feedback
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Plan + Activities (2 col) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Plan card */}
          {enrollment ? (
            <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#4CAF50]" />
                  <h2 className="font-semibold text-[#1A2E6E] text-sm">My Plan</h2>
                </div>
                <Link to="/student/billing" className="text-xs text-[#D72B2B] font-medium flex items-center gap-1">
                  Details <ChevronRight size={12} />
                </Link>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#1A2E6E]/60">Credits used</span>
                  <span className="text-sm font-semibold text-[#1A2E6E]">
                    {enrollment.credits_used} / {enrollment.credits_total}
                  </span>
                </div>
                <div className="h-2 bg-[#E8EAF0] rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full transition-all ${
                      creditsRemaining <= 1 ? 'bg-[#D72B2B]' :
                      creditsRemaining <= 3 ? 'bg-[#F5A623]' : 'bg-[#4CAF50]'
                    }`}
                    style={{ width: `${creditsPct}%` }}
                  />
                </div>
                {enrollment.expires_at && (
                  <p className="text-xs text-[#1A2E6E]/50 mb-3">
                    Expires {new Date(enrollment.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
                {creditsRemaining <= 3 && (
                  <Link
                    to={stripeEnabled ? '/student/checkout' : '/student/billing'}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#D72B2B] text-white text-sm font-semibold rounded-xl hover:bg-[#B71C1C] transition-colors"
                  >
                    Renew plan <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-[#1A2E6E]/3 rounded-xl border border-[#1A2E6E]/10 p-5 text-center">
              <CreditCard className="w-8 h-8 text-[#1A2E6E]/20 mx-auto mb-2" />
              <p className="text-sm font-medium text-[#1A2E6E] mb-1">No active plan</p>
              <p className="text-xs text-[#1A2E6E]/50 mb-4">Choose a plan to access classes and activities.</p>
              <Link
                to={stripeEnabled ? '/student/checkout' : '/student/billing'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A2E6E] text-white text-sm font-semibold rounded-xl hover:bg-[#16275C] transition-colors"
              >
                View plans <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* Pending Activities */}
          <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#F5A623]" />
                <h2 className="font-semibold text-[#1A2E6E] text-sm">Activities</h2>
                {dashboardData?.pending_activities?.length > 0 && (
                  <span className="text-xs bg-[#F5A623]/10 text-[#F5A623] px-2 py-0.5 rounded-full font-semibold">
                    {dashboardData.pending_activities.length}
                  </span>
                )}
              </div>
              <Link to="/student/activities" className="text-xs text-[#D72B2B] font-medium flex items-center gap-1">
                All <ChevronRight size={12} />
              </Link>
            </div>
            <div className="divide-y divide-[#E8EAF0]">
              {dashboardData?.pending_activities?.length > 0 ? (
                dashboardData.pending_activities.slice(0, 4).map((act, i) => (
                  <div key={act.id || i} className="flex items-center gap-3 px-5 py-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F5A623]/10 flex items-center justify-center shrink-0">
                      <BookOpen className="w-4 h-4 text-[#F5A623]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2E6E] truncate">{act.title}</p>
                      {act.review_status === 'needs_revision' ? (
                        <p className="text-xs text-[#D72B2B] font-medium">Needs revision</p>
                      ) : act.due_date ? (
                        <p className="text-xs text-[#1A2E6E]/50">
                          Due {new Date(act.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                      ) : (
                        <p className="text-xs text-[#1A2E6E]/50">Pending submission</p>
                      )}
                    </div>
                    <ChevronRight size={14} className="text-[#1A2E6E]/30 shrink-0" />
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center">
                  <CheckCircle className="w-8 h-8 text-[#4CAF50]/30 mx-auto mb-2" />
                  <p className="text-sm text-[#1A2E6E]/50">All caught up!</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick links */}
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/student/schedule"
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-[#E8EAF0] hover:border-[#1A2E6E]/30 hover:shadow-sm transition-all group"
            >
              <CalendarDays className="w-5 h-5 text-[#1A2E6E]/50 group-hover:text-[#1A2E6E]" />
              <span className="text-xs font-medium text-[#1A2E6E]/70 group-hover:text-[#1A2E6E]">Schedule 1:1</span>
            </Link>
            <Link
              to="/student/messages"
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-[#E8EAF0] hover:border-[#1A2E6E]/30 hover:shadow-sm transition-all group"
            >
              <MessageSquare className="w-5 h-5 text-[#1A2E6E]/50 group-hover:text-[#1A2E6E]" />
              <span className="text-xs font-medium text-[#1A2E6E]/70 group-hover:text-[#1A2E6E]">Messages</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
